// =========================================================================
// STRUCTURED CABLING — GOOGLE ADS LANDING PAGE (/structured-cabling)
// =========================================================================
//
// Conversion-focused landing page for business network cabling. Built from
// the same landing-page kit as ManagedItServicesLandingPage (ServiceHero,
// TrustBar, FeatureCard, LeadCta, LeadForm, MobileStickyCta) so tracking
// wiring and visual treatment stay identical across Google Ads landing
// pages. Message-matches "structured cabling tampa" / "network cabling
// installer" style ad groups.
//
// Spanish copy for this page lives in the shared dictionary
// src/i18n/es.ts (flat English-key -> Spanish map; missing keys silently
// fall back to English at runtime).

import { useT } from "../i18n";
import type { PageKey } from "../i18n";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Cable,
  Camera,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Globe,
  MapPin,
  Server,
  Wifi,
  Wrench,
} from "lucide-react";
import {
  CtaButton,
  LeadCta,
  LeadForm,
  MobileStickyCta,
  ServiceArea,
  ServiceHero,
  TrustBar,
  WhyChooseUs,
  FeatureCard,
  IndustryGrid,
  SectionLabel,
  trackCta,
} from "../SightlineWebsite";

/** Questions rendered in the FAQ accordion AND emitted as FAQPage JSON-LD. */
export const cablingFaqs: Array<{ q: string; a: string }> = [
  {
    q: "Can you run Cat6 in an existing office?",
    a: "Yes. Most of our work is retrofit cabling in occupied offices. We route cable through ceilings, conduits, and wall cavities, and we patch and seal wherever we penetrate drywall.",
  },
  {
    q: "Cat6 vs Cat6A — which do we need?",
    a: "Cat6 handles standard office networking, including gigabit and 2.5G speeds over typical office distances. Cat6A is worth the upgrade for 10G runs, long cable paths, or high-density areas. We will recommend the simpler option if it covers your needs.",
  },
  {
    q: "Can you clean up and re-terminate an existing rack?",
    a: "Yes. We remove abandoned cables, re-terminate live runs onto a proper patch panel, label both ends, and document the layout. Most racks can be cleaned up without interrupting your network for more than a short window.",
  },
  {
    q: "Do you label and test the cables you install?",
    a: "Always. Every drop is tested before it is patched in and labeled at both the wall plate and the patch panel, with the labeling documented so anyone can follow it later.",
  },
  {
    q: "Can you cable for access points and cameras too?",
    a: "Yes. We run cable to ceiling and wall locations for Wi-Fi access points and to mounting points for IP cameras, then mount and configure the equipment as part of the same project.",
  },
  {
    q: "Do you handle small jobs (a few drops)?",
    a: "Yes. Small jobs — a few new drops, a rack cleanup, a single access point run — are a normal part of our week and get the same testing and labeling as large buildouts.",
  },
];

export function StructuredCablingLanding({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();

  // Same helper as ManagedItServicesLandingPage: every primary CTA tracks and
  // then smooth-scrolls to the in-page lead form instead of navigating away.
  const scrollToLeadForm = () => {
    document
      .getElementById("structured-cabling-lead-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const whatWeInstall: Array<{ icon: typeof Cable; title: string; text: string }> = [
    {
      icon: Cable,
      title: "Cat6 & Cat6A Drops",
      text: "New cable runs for workstations, printers, access points, and equipment, sized and routed for how your space actually works.",
    },
    {
      icon: Server,
      title: "Network Rack Installation",
      text: "Wall-mount and floor racks set up cleanly, with switches, patch panels, and power arranged for service access.",
    },
    {
      icon: Wrench,
      title: "Patch Panels & Termination",
      text: "Every drop terminated on a labeled patch panel, so moves, adds, and changes no longer mean tracing mystery cables.",
    },
    {
      icon: Wifi,
      title: "Access Point Cabling",
      text: "Ceiling and wall cable runs to your Wi-Fi access point locations, placed where the coverage actually needs to be.",
    },
    {
      icon: Camera,
      title: "Security Camera Cabling",
      text: "Cable runs for IP cameras and recorders, including outdoor-rated paths where the building calls for them.",
    },
    {
      icon: Building2,
      title: "Office Buildouts & New Suites",
      text: "Cabling for new suites, remodels, and tenant improvements, planned around furniture and floor layout before the walls close up.",
    },
    {
      icon: MapPin,
      title: "Warehouse Networking",
      text: "Reliable runs to offices, shipping areas, and floor equipment, using cable paths and protection rated for the environment.",
    },
    {
      icon: Cable,
      title: "Network Cleanup & Re-Termination",
      text: "Existing racks untangled, re-terminated, relabeled, and documented, so the next change takes minutes instead of hours.",
    },
  ];

  const processSteps: Array<{ title: string; text: string }> = [
    {
      title: "Site walkthrough & estimate",
      text: "We walk the site with you, count the drops, and provide a free, itemized estimate.",
    },
    {
      title: "Cable path planning",
      text: "We map routes through ceilings, conduits, and walls, avoiding interference and building hazards.",
    },
    {
      title: "Installation",
      text: "Our technicians pull and secure every run to code, with proper support and bend radius.",
    },
    {
      title: "Termination & patch panel",
      text: "Each drop is terminated at the jack and on the patch panel, never left loose in the ceiling.",
    },
    {
      title: "Testing",
      text: "Every cable is tested for continuity and performance before anything is patched in.",
    },
    {
      title: "Labeling & documentation",
      text: "Drops are labeled at both ends and documented, so future changes are straightforward.",
    },
  ];

  const relatedLinks: Array<{ label: string; page: PageKey }> = [
    { label: "Access point installation & Wi-Fi", page: "business-wifi" },
    { label: "Security camera installation", page: "camera-installation" },
    { label: "UniFi & network installation", page: "unifi-installer" },
    { label: "Network installation services", page: "network-installation" },
  ];

  return (
    // Bottom padding on mobile only, clearing the sticky call/consult bar.
    <main className="pb-24 lg:pb-0">
      <ServiceHero
        eyebrow="Serving Tampa Bay businesses"
        title="Professional Network & Structured Cabling"
        subtitle="Cat6 and Cat6A drops, network racks, patch panels, and clean cable runs for offices, warehouses, and commercial buildouts — installed, tested, and labeled by a local Tampa Bay team."
        primaryLabel="Request a Cabling Estimate"
        onPrimary={() => {
          trackCta("cabling_estimate_click", "cabling_hero", "structured-cabling");
          scrollToLeadForm();
        }}
        ctaName="cabling-hero"
        page="structured-cabling"
      />

      <TrustBar
        items={[
          { icon: MapPin, label: "Local Tampa Bay Crew" },
          { icon: Building2, label: "Office Buildouts & Retrofits" },
          { icon: CheckCircle2, label: "Tested & Labeled Drops" },
          { icon: BadgeCheck, label: "Free Itemized Estimates" },
        ]}
      />

      {/* What we install */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("What we install")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Structured Cabling Installed the Right Way")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t(
                "From a handful of new drops to a full buildout, we install the cable, the rack, and the patch panel as one system — so your network is ready for whatever gets plugged into it next."
              )}
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {whatWeInstall.map(({ icon, title, text }) => (
              <FeatureCard key={title} icon={icon} title={t(title)} text={t(text)} />
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("How we work")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("From Walkthrough to Labeled Drops")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "Every cabling project follows the same sequence, whether it is six drops or six hundred."
            )}
          </p>
        </div>
        <ol className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {processSteps.map(({ title, text }, index) => (
            <li
              key={title}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sm font-bold text-sky-700 ring-1 ring-sky-100">
                {index + 1}
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900">{t(title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(text)}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Why proper cabling matters */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center">
          <SectionLabel>{t("Why proper cabling matters")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Mystery Cables Cost Real Time")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "We spend a lot of our week untangling the aftermath of cabling done fast: racks with no patch panels, cables with no labels, and runs nobody can account for. When a port fails or a circuit drops, someone climbs a ladder and traces cables by hand, and an outage that should take minutes to fix takes hours."
            )}
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "Clean, tested, labeled cabling is what keeps the next change — and the next outage — short."
            )}
          </p>
        </div>
      </section>

      {/* Who we help */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("Who we help")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Cabling for Every Kind of Commercial Space")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "We install and clean up cabling in working buildings of every kind across Tampa Bay."
            )}
          </p>
        </div>
        <div className="mt-10">
          <IndustryGrid
            items={[
              "Offices",
              "Warehouses",
              "Manufacturing",
              "Retail",
              "Medical & Professional Offices",
              "Multi-Tenant Buildings",
            ]}
          />
        </div>
      </section>

      {/* Related services — descriptive internal links */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Related services")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("One Team for the Whole Network")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t("Cabling is the foundation. These are the services we build on top of it.")}
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {relatedLinks.map(({ label, page }) => (
              <CtaButton
                key={page}
                variant="secondary"
                ctaName={`cabling-related-${page}`}
                page="structured-cabling"
                to={page}
                setPage={setPage}
                className="justify-between"
              >
                {t(label)}
                <ArrowRight className="h-4 w-4 shrink-0" />
              </CtaButton>
            ))}
          </div>
        </div>
      </section>

      {/* Why SightLine */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("Why SightLine Networks")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Why Businesses Choose SightLine Networks")}
          </h2>
        </div>
        <div className="mt-12">
          <WhyChooseUs
            items={[
              {
                icon: MapPin,
                title: "Local Tampa Bay Crew",
                text: "The work is done by our own technicians, not handed off to subcontracted crews you never meet.",
              },
              {
                icon: CheckCircle2,
                title: "Clean, Labeled, Tested Work",
                text: "Every drop is terminated on a panel, tested, and labeled before we call the job done.",
              },
              {
                icon: Globe,
                title: "One Partner for Cabling + Network + Cameras",
                text: "Cabling, network equipment, Wi-Fi, and cameras from the same team, so nothing gets passed between vendors.",
              },
              {
                icon: Clock3,
                title: "Responsive Estimates",
                text: "Free, itemized estimates returned quickly, so you can plan the project and the budget.",
              },
            ]}
          />
        </div>
      </section>

      {/* Real work / projects */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Project spotlights")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Recent Cabling Work")}
            </h2>
            <p className="mt-3 text-sm italic text-slate-500">
              {t("Project spotlights coming soon — real Tampa Bay project photos and details will be added here.")}
            </p>
          </div>
          {/*
            PLACEHOLDER — TODO(marketing): replace these tiles with real
            project photos (network racks before/after re-termination,
            patch panels, office buildout cable runs, technicians on-site)
            once supplied. Do not fill with stock imagery — see the handoff
            notes for this page for what to send over.
          */}
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {[
              { icon: Server, label: "Network Rack Re-Termination" },
              { icon: Cable, label: "Office Buildout Cabling" },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center"
              >
                <Icon className="h-7 w-7 text-slate-400" />
                <span className="text-sm font-medium text-slate-500">{t(label)}</span>
                <span className="text-xs uppercase tracking-wide text-slate-400">
                  {t("Photo coming soon")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service area */}
      <section className="mx-auto max-w-7xl px-6 py-20 text-center">
        <SectionLabel>{t("Service area")}</SectionLabel>
        <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          {t("Serving Businesses Throughout Tampa Bay")}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-slate-600 leading-relaxed">
          {t(
            "SightLine Networks installs and services structured cabling throughout the Tampa Bay area, including:"
          )}
        </p>
        <div className="mt-8">
          <ServiceArea cities={["Tampa", "Clearwater", "St. Petersburg", "Largo", "Pinellas Park", "Brandon"]} />
        </div>
      </section>

      <LeadCta
        title="Need cabling for a new space or a cleaner rack?"
        text="Request a free, itemized estimate. We walk the site, scope the drops, and lay out the plan before any cable is pulled."
        primaryLabel="Request a Cabling Estimate"
        onPrimary={() => {
          trackCta("cabling_estimate_click", "cabling_bottom", "structured-cabling");
          scrollToLeadForm();
        }}
        ctaName="cabling-bottom"
        page="structured-cabling"
      />

      {/* Lead form */}
      <section
        id="structured-cabling-lead-form"
        className="mx-auto max-w-3xl scroll-mt-20 px-6 py-20"
      >
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("Request Your Cabling Estimate")}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {t(
                "Tell us about the space and roughly how many drops you need. Our Tampa Bay team will follow up with an itemized estimate."
              )}
            </p>
          </div>
          <div className="mt-8">
            <LeadForm
              page="structured-cabling"
              source="structured-cabling-landing"
              formId="structured-cabling-form"
              subject="Structured Cabling"
              submitLabel="Request a Cabling Estimate"
            />
          </div>
        </div>
      </section>

      {/* FAQ — accordion plus FAQPage JSON-LD rendered from the same data */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="mb-10 max-w-2xl">
            <SectionLabel>{t("Frequently asked questions")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Structured Cabling FAQs")}
            </h2>
          </div>
          <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {cablingFaqs.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-slate-900 marker:hidden">
                  <span>{t(q)}</span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(a)}</p>
              </details>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-600">
              {t("Have a question we did not cover? Send us the details and we will answer directly — no obligation.")}
            </p>
            <div className="mt-5">
              <CtaButton
                onClick={() => {
                  trackCta("cabling_estimate_click", "cabling_faq_contact", "structured-cabling");
                  scrollToLeadForm();
                }}
                ctaName="cabling-faq-contact"
                page="structured-cabling"
              >
                {t("Request a Cabling Estimate")} <ArrowRight className="h-4 w-4" />
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      <MobileStickyCta
        onConsultClick={() => {
          trackCta("cabling_estimate_click", "cabling_sticky", "structured-cabling");
          scrollToLeadForm();
        }}
        ctaName="cabling"
        page="structured-cabling"
        label="Free Cabling Estimate"
      />
    </main>
  );
}